// Training 세션 raw status 어휘의 단일 소스. training-infra `lifecycle.py` 의 세션 상태와 1:1로
// 맞춘다. 상태 칩·상태 필터·액션 게이트(취소/재시도)·로그 폴링이 모두 이 파일에서 파생된다.
// 그래서 백엔드가 상태를 추가하면 이 파일만 고친다.

// 아직 일이 남은 상태. 취소 버튼과 로그 실시간 폴링을 여는 기준이다.
export const ACTIVE_SESSION_STATUSES = [
  "pending",
  "queued",
  "dispatched",
  "running",
  "cancel_requested",
  "waiting_for_capacity",
  "waiting_for_image",
  "waiting_for_artifact",
  "sweep_held",
  "retrying_transient_failure",
] as const;

// 더 진행되지 않는 상태.
export const TERMINAL_SESSION_STATUSES = [
  "succeeded",
  "failed",
  "cancelled",
  "failed_user_action_required",
  "dispatch_failed",
  "build_failed",
  "image_publish_failed",
  "failed_infra_exhausted",
] as const;

export type SessionStatus =
  | (typeof ACTIVE_SESSION_STATUSES)[number]
  | (typeof TERMINAL_SESSION_STATUSES)[number];

const ACTIVE = new Set<string>(ACTIVE_SESSION_STATUSES);
const TERMINAL = new Set<string>(TERMINAL_SESSION_STATUSES);

// 외부 응답에서 함께 허용하는 상태 별칭을 정규 상태로 바꾼다.
const STATUS_ALIASES: Record<string, SessionStatus> = {
  completed: "succeeded",
  canceled: "cancelled",
  error: "failed",
};

// 대소문자 차이와 별칭을 정규 상태 문자열로 바꾼다. 아래 판정은 모두 이 값을 쓴다.
function canonicalStatus(status: string): string {
  const lowered = status.toLowerCase();
  return STATUS_ALIASES[lowered] ?? lowered;
}

export function isActiveSessionStatus(status: string | null): boolean {
  return status !== null && ACTIVE.has(canonicalStatus(status));
}

// 재시도 가능 여부는 서버가 최종 판정한다. training 은 활성 상태만 막는다(blocklist).
// registry backend 는 상태 게이트 없이 전달한다. 그래서 성공을 뺀 모든 종료 상태에서 버튼을
// 연다. 서버가 거절하면 409 를 사용자에게 안내한다.
export function isRetryableSessionStatus(status: string | null): boolean {
  if (status === null) {
    return false;
  }
  const canonical = canonicalStatus(status);
  return TERMINAL.has(canonical) && canonical !== "succeeded";
}

// 이어서 학습(resume)을 여는 기준. 재시도와 다르게 성공한 세션도 대상이다. 상류는 "부모가
// 터미널 상태"만 요구한다. 실행 중인 부모를 이어서 학습하면 한 MLflow run 에 writer 가 둘이 된다.
// 성공한 학습을 더 학습하는 것도 유효한 사용법이다. 위 TERMINAL_SESSION_STATUSES 8종은 상류
// `lifecycle.TERMINAL_SESSION` 과 같은 집합이다. 그래서 게이트가 상류와 같다.
//
// 나머지 거절 조건(train step 2개 이상·MLflow run 부재·체크포인트 없음)은 상태로 알 수 없다.
// 그래서 서버가 판정한다. 화면은 409/404 를 받아 안내한다.
export function isResumableSessionStatus(status: string | null): boolean {
  return status !== null && TERMINAL.has(canonicalStatus(status));
}

// 세션 상태를 단순 6종(+미지정)으로 묶는다. training raw 어휘는 세분화되어 있다. 그래서 목록·상세
// 칩과 상태 필터가 이 묶음을 공유한다. key 는 라벨이 아니라 안정 식별자다.
export type SessionStatusKey =
  | "waiting"
  | "in_progress"
  | "succeeded"
  | "failed"
  | "action_required"
  | "cancelled"
  | "unknown";

// 이 모듈은 색을 정하지 않는다. 카테고리 이름만 제공한다. c6 status.job 토큰으로 색을 칠하는
// 일은 표시층(`components/status/SessionStatusChip`)이 소유한다. 이 모듈은 c6 에 의존하지 않는다.
export type SessionSimpleStatus = {
  key: SessionStatusKey;
  label: string;
};

// Record<SessionStatus, ...> 이다. 그래서 어휘에 상태를 추가하면 여기를 채울 때까지 타입 검사가
// 실패한다. 목록 하나를 빠뜨려 칩과 게이트가 달라지는 문제를 타입으로 막는다.
const STATUS_KEY: Record<SessionStatus, SessionStatusKey> = {
  // 세션이 아직 자기 일을 시작하지 못한 상태. 자리·이미지·아티팩트를 기다리거나 큐에 있다.
  // 실행 축(ACTIVE/TERMINAL)이 아니라 표시 축이다. 그래서 취소·재시도 게이트에는 영향이 없다.
  pending: "waiting",
  queued: "waiting",
  waiting_for_capacity: "waiting",
  waiting_for_image: "waiting",
  waiting_for_artifact: "waiting",
  sweep_held: "waiting",
  dispatched: "in_progress",
  running: "in_progress",
  cancel_requested: "in_progress",
  retrying_transient_failure: "in_progress",
  succeeded: "succeeded",
  failed: "failed",
  cancelled: "cancelled",
  // 사용자가 설정을 고쳐 다시 실행하면 되는 실패다. 그래서 일반 실패와 분리해 보여준다.
  failed_user_action_required: "action_required",
  dispatch_failed: "failed",
  build_failed: "failed",
  image_publish_failed: "failed",
  failed_infra_exhausted: "failed",
};

const STATUS_KEY_META: Record<SessionStatusKey, { label: string }> = {
  waiting: { label: "대기" },
  in_progress: { label: "진행 중" },
  succeeded: { label: "성공" },
  failed: { label: "실패" },
  action_required: { label: "조치 필요" },
  cancelled: { label: "취소" },
  unknown: { label: "기타" },
};

// 조회 시점에는 어휘 밖 문자열도 들어온다. 그래서 key 타입을 넓혀 읽는다. 선언부는
// Record<SessionStatus, ...> 그대로다. 그래서 어휘를 추가할 때 누락은 계속 타입 검사가 잡는다.
const STATUS_KEY_LOOKUP: Record<string, SessionStatusKey | undefined> = STATUS_KEY;

export function sessionSimpleStatus(rawStatus: string): SessionSimpleStatus {
  const key = STATUS_KEY_LOOKUP[canonicalStatus(rawStatus)] ?? "unknown";
  return { key, ...STATUS_KEY_META[key] };
}

// "전체" 센티넬. 상태 필터 미적용을 뜻하는 빈 값.
export const ALL_SUBMIT_STATUS = "";

// "취소 제외" 토글이 보내는 카테고리. `?exclude_status_group=` 값이다. 상태 필터와 같은 축이다.
// 그래서 서버는 둘을 동시에 받지 않는다(422).
export const CANCELLED_STATUS_GROUP: SessionStatusKey = "cancelled";

// 카테고리 안에서 raw status 를 구분하는 세부 라벨. Record 를 완전하게 유지한다. 그래서 어휘가
// 늘면 여기를 채울 때까지 타입 검사가 실패한다.
const STATUS_DETAIL_LABELS: Record<SessionStatus, string> = {
  pending: "제출 대기",
  queued: "실행 대기열",
  dispatched: "실행 요청 전달",
  running: "실행 중",
  cancel_requested: "취소 요청 중",
  waiting_for_capacity: "리소스 대기",
  waiting_for_image: "이미지 대기",
  waiting_for_artifact: "Artifact 대기",
  sweep_held: "Sweep 보류",
  retrying_transient_failure: "일시 오류 재시도 중",
  succeeded: "성공",
  failed: "실패",
  cancelled: "취소",
  failed_user_action_required: "사용자 조치 필요",
  dispatch_failed: "실행 요청 전달 실패",
  build_failed: "빌드 실패",
  image_publish_failed: "이미지 게시 실패",
  failed_infra_exhausted: "인프라 재시도 소진",
};

// 카테고리 나열 순서. 세션 수명주기 순서와 같다. unknown 은 raw 구성원이 없다(어휘 밖 문자열을
// 읽을 때만 쓰는 key). 그래서 이 목록에 없다. 이 목록은 목록 필터가 고를 수 있는 값 집합이기도
// 하다(URL 검증).
export const SUBMIT_STATUS_CATEGORIES: readonly SessionStatusKey[] = [
  "waiting",
  "in_progress",
  "succeeded",
  "failed",
  "action_required",
  "cancelled",
];

const VOCABULARY = [...ACTIVE_SESSION_STATUSES, ...TERMINAL_SESSION_STATUSES];

function categoryMembers(category: SessionStatusKey): readonly SessionStatus[] {
  return VOCABULARY.filter((status) => STATUS_KEY[status] === category);
}

// "{카테고리}: {세부}". 카테고리에 구성원이 하나뿐이면 "성공: 성공" 처럼 중복된다. 그때는
// 카테고리 이름만 반환한다. 접두어는 카테고리 라벨을 그대로 쓴다. 그래서 필터에서 고른 이름이
// 행의 칩 앞에 그대로 보인다.
function categoryDetailLabel(status: SessionStatus): string {
  const category = STATUS_KEY[status];
  const categoryLabel = STATUS_KEY_META[category].label;
  return categoryMembers(category).length === 1
    ? categoryLabel
    : `${categoryLabel}: ${STATUS_DETAIL_LABELS[status]}`;
}

// 표 행의 상태 칩. 카테고리와 세부를 함께 보여준다. 칩이 카테고리만 표시하면 사용자는 대기
// 이유(자리 대기인지 이미지 대기인지)를 행에서 알 수 없다. 어휘 밖 raw 는 sessionSimpleStatus 와
// 같게 "기타" 로 표시한다.
export function sessionDetailedStatus(rawStatus: string): SessionSimpleStatus {
  const canonical = canonicalStatus(rawStatus);
  const simple = sessionSimpleStatus(rawStatus);
  if (simple.key === "unknown") {
    return simple;
  }
  return { ...simple, label: categoryDetailLabel(canonical as SessionStatus) };
}

// 카테고리 접두 없는 세부 라벨. 카테고리 이름 외에 더할 정보가 없으면 null 이다. 예: `failed` 의
// 세부는 "실패" 라 카테고리와 같다. 카테고리를 이미 표시하는 자리(상태 칩 옆)가 쓴다. 그래서
// "실패: 실패" 처럼 같은 말을 되풀이하지 않는다.
export function sessionStatusDetail(rawStatus: string): string | null {
  const canonical = canonicalStatus(rawStatus);
  const key = STATUS_KEY_LOOKUP[canonical];
  if (key === undefined) {
    return null;
  }
  const detail = STATUS_DETAIL_LABELS[canonical as SessionStatus];
  return detail === STATUS_KEY_META[key].label ? null : detail;
}

// 상태 필터 옵션. 표시 카테고리 단위다. raw 18종을 평면으로 나열하면 고르기 어렵다. 그리고
// 사용자가 표에서 보는 칩 어휘와도 다르다. 값은 카테고리 key 이고 그대로 `?status_group=` 으로
// 보낸다. registry 가 멤버 raw status 로 fan-out 한다(계약: docs/specs/training-session-status-group.md).
// 행의 세부 사유는 칩의 "{카테고리}: {세부}" 가 표시한다.
export const SUBMIT_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: ALL_SUBMIT_STATUS, label: "전체 상태" },
  ...SUBMIT_STATUS_CATEGORIES.map((category) => ({
    value: category,
    label: STATUS_KEY_META[category].label,
  })),
];
